import { IList, IStatusTimelineItem, StatusTimelineItemState, mapToCharPadder, mapToDocumentFile } from '@aston/foundation';

import { IImportBooleanMapping, IImportCivilityMapping, IImportColumn, IImportConfiguration, IImportCountryMapping, IImportCurrenciesMapping, IImportDocumentTypeMapping, IImportErrorColumnRowModel, IImportErrorGroupBasicModel, IImportFileColumnMapping, IImportFileConfiguration, IImportFileProviderReportErrorLineItem, IImportFileReportErrorLineItem, IImportHistoryItem, IImportHistoryItemBase, IImportItem, IImportLanguageMapping, IImportOperationTypeMapping, IImportPaymentTypeMapping, IImportProviderErrorColumnRowModel, IImportProviderErrorGroupBasicModel, IImportProvidersHistoryItem, IImportRecoveryAdvisorMapping, IImportSectorMapping } from '../models';
import { IImportBooleanMappingApi, IImportCivilityMappingApi, IImportConfigurationApi, IImportCountryMappingApi, IImportCurrencyMappingApi, IImportDocumentTypeMappingApi, IImportErrorColumnRowModelApi, IImportFileColumnMappingApi, IImportFileConfigurationApi, IImportFileProviderReportErrorLineItemApi, IImportFileReportErrorLineItemApi, IImportHistoryItemApi, IImportHistoryItemBaseApi, IImportItemApi, IImportLanguageMappingApi, IImportOperationTypeMappingApi, IImportPaymentTypeMappingApi, IImportProviderErrorColumnRowModelApi, IImportProvidersHistoryItemApi, IImportRecoveryAdvisorMappingApi, IImportSectorMappingApi } from '../models/api';
import { ImportConfigurationStatus, ImportDateFormat, ImportErrorHandling, ImportInstanceDetailAction, ImportStatus, ImportType } from '../enums';


function emptyIfNone<T>(value: T): T {
	return value?.toString() === 'None' ? '' as unknown as T : value;
}

export function mapToImportConfigurationModel(apiModel: IImportFileConfigurationApi): IImportFileConfiguration {
	const knownDateFormat = Object.entries(ImportDateFormat).find(a => a[1] === apiModel.dateFormatTemplate);

	return {
		...apiModel,
		columnSeparator: emptyIfNone(apiModel.columnSeparator),
		decimalSeparator: emptyIfNone(apiModel.decimalSeparator),
		importConfigurationStatus: emptyIfNone(apiModel.importConfigurationStatus),
		thousandSeparator: emptyIfNone(apiModel.thousandSeparator),
		dateFormatTemplate: emptyIfNone(knownDateFormat ? apiModel.dateFormatTemplate : ImportDateFormat.None),
		rejectWholeFile: apiModel.rejectWholeFile === true ?
			ImportErrorHandling.Complete :
			ImportErrorHandling.BestEffort
	}
}

export function mapToImportItemModel(apiModel: IImportItemApi, genericErrorMessage: string): IImportItem {
	const toReturn = {
		id: apiModel.importInstanceModel.id,
		type: apiModel.importConfigurationModel.importType,
		status: apiModel.importInstanceModel.status,
		file: !apiModel.importInstanceModel.document ? null : mapToDocumentFile(apiModel.importInstanceModel.document),
		report: !apiModel.importInstanceModel.reportDocument ? null : mapToDocumentFile(apiModel.importInstanceModel.reportDocument),
		completedDocument: apiModel.importInstanceModel.accountingDocumentsCompletedDocument,
		currentStep: apiModel.importConfigurationModel.currentStep,

		fileExtract: {
			headers: apiModel.importInstanceModel.excerptHeaders || [],
			lines: apiModel.importInstanceModel.excerptRows || [],
		},

		importFileConfiguration: {
			...mapToImportConfigurationModel(apiModel.importConfigurationModel),
		},

		columnsMapping: apiModel.importColumnMappingModels ?
			apiModel.importColumnMappingModels.map(model => ({
				columnName: model.columnName,
				mapsTo: model.importColumn
			})) : [],

		documentTypesMapping: apiModel.importAccountingDocumentTypeMappingModels ?
			apiModel.importAccountingDocumentTypeMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.accountingDocumentType
			})) : [],

		paymentTypesMapping: apiModel.importPaymentTypeMappingModels ?
			apiModel.importPaymentTypeMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.paymentType
			})) : [],

		operationTypesMapping: apiModel.importOperationTypeMappingModels ?
			apiModel.importOperationTypeMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.operationType
			})) : [],

		currenciesMapping: apiModel.importCurrencyMappingModels ?
			apiModel.importCurrencyMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.currencyCode
			})) : [],

		booleanMapping: apiModel.importBooleanMappingModels ?
			apiModel.importBooleanMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.booleanValue
			})) : [],

		languagesMapping: apiModel.importLanguageMappingModels ?
			apiModel.importLanguageMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				isMapped: model.isMapped,
				mapsTo: model.languageCode
			})) : [],

		countriesMapping: apiModel.importCountryMappingModels ?
			apiModel.importCountryMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				isMapped: model.isMapped,
				mapsTo: model.countryCode
			})) : [],

		recoveryAdvisorsMapping: apiModel.importRecoveryAdvisorsMappingModels ?
			apiModel.importRecoveryAdvisorsMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				isMapped: model.isMapped,
				mapsTo: model.userId
			})) : [],

		civilityMapping: apiModel.importCivilityMappingModels ?
			apiModel.importCivilityMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.civility
			})) : [],
		
		sectorMapping: apiModel.importSectorMappingModels ?
			apiModel.importSectorMappingModels.map(model => ({
				mappedValue: model.mappedValue,
				mapsTo: model.sector
			})) : [],

		synthesis: {
			totalReadLineCount: apiModel.importInstanceModel.totalReadLineCount || 0,
			importedLineCount: (apiModel.importInstanceModel.addCount || 0) + (apiModel.importInstanceModel.updateCount || 0) + (apiModel.importInstanceModel.deleteCount || 0),
			notImportedLineCount: apiModel.importInstanceModel.notImportedLineCount || 0,
			addCount: apiModel.importInstanceModel.addCount || 0,
			updateCount: apiModel.importInstanceModel.updateCount || 0,
			deleteCount: apiModel.importInstanceModel.deleteCount || 0,
			emptyMandatoryColumns: apiModel.importInstanceModel.emptyMandatoryColumns || [],
			accountingDocumentsCompletedDocumentId: apiModel.importInstanceModel.accountingDocumentsCompletedDocumentId || 0,
			accountingDocumentsCompletedDocument:  apiModel.importInstanceModel.accountingDocumentsCompletedDocument || null,
			accountingDocumentsCompletedCount:  apiModel.importInstanceModel.accountingDocumentsCompletedCount || 0
		}
	};

	if (toReturn.importFileConfiguration.importConfigurationStatus === ImportConfigurationStatus.Error) {
		toReturn.importFileConfiguration.extraMessage = genericErrorMessage;
	}

	return toReturn;
}

export function mapToImportHistoryListBaseApi(apiModel: IImportHistoryItemBaseApi): IImportHistoryItemBase {
	return {
		id: apiModel.id,
		functionalId: mapToCharPadder(apiModel.id, 8),
		importStatus: apiModel.importStatus,
		createdAt: apiModel.createdAt,
		importedDocument: !apiModel.importedDocument ? null : mapToDocumentFile(apiModel.importedDocument),
		reportDocument: !apiModel.reportDocument ? null : mapToDocumentFile(apiModel.reportDocument),
	};
}

export function mapToImportHistoryListApi(apiModel: IImportHistoryItemApi): IImportHistoryItem {
	return {
		...mapToImportHistoryListBaseApi(apiModel),
		type: apiModel.type,
		createdBy: apiModel.createdBy,
		readLineCount: apiModel.readLineCount,
		importedLineCount: apiModel.importedLineCount,
		accountingDocumentsCompletedDocument: apiModel.accountingDocumentsCompletedDocument,
	};
}

export function mapToImportProvidersHistoryListApi(apiModel: IImportProvidersHistoryItemApi): IImportProvidersHistoryItem {
	return {
		...mapToImportHistoryListBaseApi(apiModel),
		provider: apiModel.provider,
	};
}

export function mapToImportFileConfigurationApi(model: IImportFileConfiguration): IImportFileConfigurationApi {
	return {
		...model,
		rejectWholeFile: model.rejectWholeFile === ImportErrorHandling.Complete,
	};
}

export function mapToImportFileColumnMappingApi(model: IImportFileColumnMapping): IImportFileColumnMappingApi {
	return {
		columnName: model.columnName,
		importColumn: model.mapsTo,
	};
}

export function mapToImportDocumentTypeMappingApi(model: IImportDocumentTypeMapping): IImportDocumentTypeMappingApi {
	return {
		mappedValue: model.mappedValue,
		accountingDocumentType: model.mapsTo
	};
}

export function mapToImportPaymentTypeMappingApi(model: IImportPaymentTypeMapping): IImportPaymentTypeMappingApi {
	return {
		mappedValue: model.mappedValue,
		paymentType: model.mapsTo
	};
}

export function mapToImportOperationTypeMappingApi(model: IImportOperationTypeMapping): IImportOperationTypeMappingApi {
	return {
		mappedValue: model.mappedValue,
		operationType: model.mapsTo
	};
}

export function mapToImportInvoicingCurrencyMappingApi(model: IImportCurrenciesMapping): IImportCurrencyMappingApi {
	return {
		mappedValue: model.mappedValue,
		currencyCode: model.mapsTo
	};
}

export function mapToImportRecoveryAdvisorMappingApi(model: IImportRecoveryAdvisorMapping): IImportRecoveryAdvisorMappingApi {
	return {
		mappedValue: model.mappedValue,
		isMapped: model.isMapped,
		// explicitly not mapped ? The API wants null to be send.
		userId: model.mapsTo <= 0 ? null : model.mapsTo
	};
}

export function mapToImportCivilityMappingApi(model: IImportCivilityMapping): IImportCivilityMappingApi {
	return {
		mappedValue: model.mappedValue,
		civility: model.mapsTo
	};
}

export function mapToImportLanguageMappingApi(model: IImportLanguageMapping): IImportLanguageMappingApi {
	return {
		mappedValue: model.mappedValue,
		languageCode: model.mapsTo,
		isMapped: model.isMapped
	};
}

export function mapToImportSectorMappingApi(model: IImportSectorMapping): IImportSectorMappingApi {
	return {
		mappedValue: model.mappedValue,
		sector: model.mapsTo
	};
}

export function mapToImportBooleanMappingApi(model: IImportBooleanMapping): IImportBooleanMappingApi {
	return {
		mappedValue: model.mappedValue,
		booleanValue: model.mapsTo
	};
}

export function mapToImportCountryMappingApi(model: IImportCountryMapping): IImportCountryMappingApi {
	return {
		mappedValue: model.mappedValue,
		isMapped: model.isMapped,

		// explicitly not mapped ? The API wants null to be send.
		countryCode: model.mapsTo === '' ? null : model.mapsTo
	};
}

// TODO: Fix me when adding Providers report error modal
export function mapToReportErrorColumnBasicItemModel(model: IImportErrorColumnRowModelApi, columnDefs: IImportColumn[]): IImportErrorGroupBasicModel {
	const def = columnDefs.find(c => c.importColumn === model.column);
	// Bug 22859: [ACC] [MO-IMPORT-ETAPE 5] : Erreur de traduction dans une info-bulle
	// could be neither a column, nor an extradata, in the None case
	// where the error is about the whole line
	return {
		...model,
		isExtraData: def?.isExtraData,
		label: def?.isExtraData ? def.label : ('Import.Creation.DataMapping.Columns.' + model.column),
		firstActionDetail: model.firstImportInstanceDetailAction === ImportInstanceDetailAction.None ? null : model.firstImportInstanceDetailAction
	};
}

// TODO: Fix me when adding Providers report error modal
export function mapToProviderReportErrorColumnBasicItemModel(model: IImportProviderErrorColumnRowModelApi, _columnDefs: IImportColumn[]): IImportProviderErrorGroupBasicModel {
	// const def = columnDefs.find(c => c.importColumn === model.column);
	// Bug 22859: [ACC] [MO-IMPORT-ETAPE 5] : Erreur de traduction dans une info-bulle
	// could be neither a column, nor an extradata, in the None case
	// where the error is about the whole line
	return {
		errorCode: model.code,
		errorCount: model.errorCount,
		firstMessage: model.firstMessage,
		// isExtraData: def?.isExtraData,
		// label: def?.isExtraData ? def.label : ('Import.Creation.DataMapping.Columns.' + model.column),
		// firstActionDetail: model.firstImportInstanceDetailAction === ImportInstanceDetailAction.None ? null : model.firstImportInstanceDetailAction
	};
}

export function mapToReportErrorColumnItemModel(model: IImportErrorColumnRowModelApi, columnDefs: IImportColumn[]): IImportErrorColumnRowModel {
	return {
		...mapToReportErrorColumnBasicItemModel(model, columnDefs),
		isReportErrorLineListOpened: false,
		reportErrorLines: { items: [] },
		isReportErrorLineListInitialized: false,
		isReportErrorLineListLoading: false,
		reportErrorLinesListError: null,
		reportErrorLinesListPaging: null
	};
}

export function mapToProviderReportErrorColumnItemModel(model: IImportProviderErrorColumnRowModelApi, columnDefs: IImportColumn[]): IImportProviderErrorColumnRowModel {
	return {
		...mapToProviderReportErrorColumnBasicItemModel(model, columnDefs),
		isReportErrorLineListOpened: false,
		reportErrorLines: { items: [] },
		isReportErrorLineListInitialized: false,
		isReportErrorLineListLoading: false,
		reportErrorLinesListError: null,
		reportErrorLinesListPaging: null
	};
}

export function mapToReportErrorLineItemModel(model: IImportFileReportErrorLineItemApi): IImportFileReportErrorLineItem {
	return {
		...model,
	};
}

export function mapToProviderReportErrorLineItemModel(model: IImportFileProviderReportErrorLineItemApi): IImportFileProviderReportErrorLineItem {
	return {
		...model,
	};
}

export function sortItemsByFileColumnName(
	items: IImportErrorColumnRowModel[], mapping: IImportFileColumnMapping[], desc: boolean): IImportErrorColumnRowModel[] {
	const order = desc ? -1 : 1;
	// order column defs by their column names
	const columnsOrder = mapping.sort((a, b) => a.columnName < b.columnName ? -1 : 1).map(m => m.mapsTo);
	// use it to order mappings
	const columnsMapping = items.sort((
		a, b) => columnsOrder.indexOf(a.column) < columnsOrder.indexOf(b.column) ? (-(1 * order)) : (1 * order));
	return columnsMapping;
}

export function mapToImportConfiguration(model: IImportConfigurationApi): IImportConfiguration {
	return {
		...model,
		importColumns: model.importColumns.map(i => ({ ...i,
			importColumns: i.importColumns.map(ii => ({ ...ii,
				label: ii.isExtraData ? (ii.prefixLabel + ii.label) : createMapToImportConfigTradKey(i.importType, ii.importColumn)
			}))
		}))
	}
}

export function createMapToImportConfigTradKey(importType, importColumn) {
	const base = 'Import.Creation.DataMapping.Columns.';

	if (importType === ImportType.Contact && !!importColumn.split('DunningContact')[1]) {
		return base + 'Contact' + importColumn.split('DunningContact')[1]
	}

	return base + importColumn;
}

export function mapToImportTimeline(list: IList<IImportHistoryItem>, maxItems: number, reverse: boolean): IList<IStatusTimelineItem> {
	const maxValue = Math.max(...list.items.map(i => i.readLineCount));
	const states = {
		[ImportStatus.Done]: StatusTimelineItemState.Success,
		[ImportStatus.Draft]: StatusTimelineItemState.Unknown,
		[ImportStatus.Error]: StatusTimelineItemState.Danger,
		[ImportStatus.Running]: StatusTimelineItemState.Info,
		[ImportStatus.Warning]: StatusTimelineItemState.Warning,
	};
	const items = list.items.slice(0, maxItems).map(item => ({
		...item,
		label: undefined,
		state: states[item.importStatus],
		height: item.readLineCount / maxValue,
	}));
	if (reverse) items.reverse();
	return {
		...list,
		items,
	}
}

export function mapToImportProvidersTimeline(list: IList<IImportProvidersHistoryItem>, maxItems: number, reverse: boolean): IList<IStatusTimelineItem> {
	const states = {
		[ImportStatus.Done]: StatusTimelineItemState.Success,
		[ImportStatus.Draft]: StatusTimelineItemState.Unknown,
		[ImportStatus.Error]: StatusTimelineItemState.Danger,
		[ImportStatus.Running]: StatusTimelineItemState.Info,
		[ImportStatus.Warning]: StatusTimelineItemState.Warning,
	};
	const items = list.items.slice(0, maxItems).map(item => ({
		...item,
		label: undefined,
		state: states[item.importStatus],
		height: 1,
	}));
	if (reverse) items.reverse();
	return {
		...list,
		items,
	}
}